import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import { NumberUtils } from '@/utils/numbers.utils';

export type ProcessingStatus = 'pending' | 'processing' | 'success' | 'error';

export interface VarBillingRow {
  externalContractId: string;
  customerCode: string;
  productCode: string;
  quantity: number | undefined;
  price: number | undefined;
  net: number | undefined;
  comment: string;
  errors?: string;
  status?: ProcessingStatus;
}

enum ColumnNames {
  ExternalContractId = 'External Contract ID',
  CustomerCode = 'Customer Code',
  ProductCode = 'Product Code',
  Quantity = 'Quantity',
  Price = 'Price',
  Net = 'Net',
  Comment = 'Comment',
}

const normalizeColumnName = (name: string): string => {
  return name.replace(/\s+/g, '').replace(/\(.*?\)/g, '').toLowerCase();
};

const columnAliases: Record<string, string[]> = {
  customercode: ['customercode', 'accountcode'],
  productcode: ['productcode', 'stockcode'],
  price: ['price', 'rate'],
  net: ['net', 'nettotal'],
};

const findColumnValue = (row: any, targetColumn: string): string => {
  const normalizedTarget = normalizeColumnName(targetColumn);
  const aliases = columnAliases[normalizedTarget] || [normalizedTarget];

  for (const [key, value] of Object.entries(row)) {
    const normalizedKey = normalizeColumnName(key);
    if (aliases.includes(normalizedKey)) {
      return value as string || '';
    }
  }
  return '';
};

const parseCSV = (file: File, columnMappings?: Map<string, string>): Promise<VarBillingRow[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        try {
          const reverseMappings = new Map<string, string>();
          if (columnMappings && columnMappings.size > 0) {
            columnMappings.forEach((targetField, csvColumn) => {
              reverseMappings.set(targetField, csvColumn);
            });
          }

          const data = results.data.map((row: any) => {
            let externalContractId: string;
            let customerCode: string;
            let productCode: string;
            let quantityStr: string;
            let priceStr: string;
            let netStr: string;
            let comment: string;

            if (reverseMappings.size > 0) {
              externalContractId = row[reverseMappings.get('externalContractId') || ''] || '';
              customerCode = row[reverseMappings.get('customerCode') || ''] || '';
              productCode = row[reverseMappings.get('productCode') || ''] || '';
              quantityStr = row[reverseMappings.get('quantity') || ''] || '';
              priceStr = row[reverseMappings.get('price') || ''] || '';
              netStr = row[reverseMappings.get('net') || ''] || '';
              comment = row[reverseMappings.get('comment') || ''] || '';
            } else {
              externalContractId = findColumnValue(row, ColumnNames.ExternalContractId);
              customerCode = findColumnValue(row, ColumnNames.CustomerCode);
              productCode = findColumnValue(row, ColumnNames.ProductCode);
              quantityStr = findColumnValue(row, ColumnNames.Quantity);
              priceStr = findColumnValue(row, ColumnNames.Price);
              netStr = findColumnValue(row, ColumnNames.Net);
              comment = findColumnValue(row, ColumnNames.Comment);
            }

            let errors = '';
            
            if (!customerCode) {
              errors = 'Customer Code is required.';
            } else if (customerCode.length > 100) {
              errors = 'Customer Code must not exceed 100 characters.';
            }
            
            if (!productCode) {
              errors = errors ? `${errors} Product Code is required.` : 'Product Code is required.';
            }
            
            if (!quantityStr) {
              errors = errors ? `${errors} Quantity is required.` : 'Quantity is required.';
            }
            
            if (comment && comment.length > 200) {
              const commentError = 'Comment must not exceed 200 characters.';
              errors = errors ? `${errors} ${commentError}` : commentError;
            }

            const quantity = quantityStr ? NumberUtils.parseIntegerInput(quantityStr) : undefined;
            if (quantityStr && (isNaN(quantity!) || quantity! <= 0)) {
              const quantityError = 'Quantity must be a valid positive integer.';
              errors = errors ? `${errors} ${quantityError}` : quantityError;
            }

            const price = priceStr ? NumberUtils.parseNumericInput(priceStr) : undefined;
            if (priceStr && isNaN(price!)) {
              errors = errors ? `${errors} Price must be a valid number.` : 'Price must be a valid number.';
            }

            const net = netStr ? NumberUtils.parseNumericInput(netStr) : undefined;
            if (netStr && isNaN(net!)) {
              errors = errors ? `${errors} Net must be a valid number.` : 'Net must be a valid number.';
            }

            return {
              externalContractId,
              customerCode,
              productCode,
              quantity,
              price,
              net,
              comment,
              errors,
              status: errors ? ('error' as const) : ('pending' as const),
            };
          });
          resolve(data.filter(row => 
            row.externalContractId || row.customerCode || row.productCode
          ));
        } catch {
          reject(new Error('Failed to parse CSV file'));
        }
      },
      error: () => reject(new Error('Failed to read CSV file')),
    });
  });
};

const parseExcel = async (file: File, columnMappings?: Map<string, string>): Promise<VarBillingRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          resolve([]);
          return;
        }

        const reverseMappings = new Map<string, string>();
        if (columnMappings && columnMappings.size > 0) {
          columnMappings.forEach((targetField, csvColumn) => {
            reverseMappings.set(targetField, csvColumn);
          });
        }

        const [headers, ...rows] = jsonData as any[][];
        const result = rows.map((row: any[]) => {
          const rowObj: any = {};
          headers.forEach((header: string, index: number) => {
            rowObj[header] = row[index] || '';
          });

          let externalContractId: string;
          let customerCode: string;
          let productCode: string;
          let quantityStr: string;
          let priceStr: string;
          let netStr: string;
          let comment: string;

          if (reverseMappings.size > 0) {
            externalContractId = rowObj[reverseMappings.get('externalContractId') || ''] || '';
            customerCode = rowObj[reverseMappings.get('customerCode') || ''] || '';
            productCode = rowObj[reverseMappings.get('productCode') || ''] || '';
            quantityStr = rowObj[reverseMappings.get('quantity') || ''] || '';
            priceStr = rowObj[reverseMappings.get('price') || ''] || '';
            netStr = rowObj[reverseMappings.get('net') || ''] || '';
            comment = rowObj[reverseMappings.get('comment') || ''] || '';
          } else {
            externalContractId = findColumnValue(rowObj, ColumnNames.ExternalContractId);
            customerCode = findColumnValue(rowObj, ColumnNames.CustomerCode);
            productCode = findColumnValue(rowObj, ColumnNames.ProductCode);
            quantityStr = findColumnValue(rowObj, ColumnNames.Quantity);
            priceStr = findColumnValue(rowObj, ColumnNames.Price);
            netStr = findColumnValue(rowObj, ColumnNames.Net);
            comment = findColumnValue(rowObj, ColumnNames.Comment);
          }

          let errors = '';
          
          if (!customerCode) {
            errors = 'Customer Code is required.';
          } else if (customerCode.length > 100) {
            errors = 'Customer Code must not exceed 100 characters.';
          }
          
          if (!productCode) {
            errors = errors ? `${errors} Product Code is required.` : 'Product Code is required.';
          }
          
          if (!quantityStr) {
            errors = errors ? `${errors} Quantity is required.` : 'Quantity is required.';
          }
          
          if (comment && comment.length > 200) {
            const commentError = 'Comment must not exceed 200 characters.';
            errors = errors ? `${errors} ${commentError}` : commentError;
          }

          const quantity = quantityStr ? NumberUtils.parseIntegerInput(quantityStr) : undefined;
          if (quantityStr && (isNaN(quantity!) || quantity! <= 0)) {
            const quantityError = 'Quantity must be a valid positive integer.';
            errors = errors ? `${errors} ${quantityError}` : quantityError;
          }

          const price = priceStr ? NumberUtils.parseNumericInput(priceStr) : undefined;
          if (priceStr && isNaN(price!)) {
            errors = errors ? `${errors} Price must be a valid number.` : 'Price must be a valid number.';
          }

          const net = netStr ? NumberUtils.parseNumericInput(netStr) : undefined;
          if (netStr && isNaN(net!)) {
            errors = errors ? `${errors} Net must be a valid number.` : 'Net must be a valid number.';
          }

          return {
            externalContractId,
            customerCode,
            productCode,
            quantity,
            price,
            net,
            comment,
            errors,
            status: errors ? ('error' as const) : ('pending' as const),
          };
        }).filter(row => 
          row.externalContractId || row.customerCode || row.productCode
        );

        resolve(result);
      } catch {
        reject(new Error('Failed to parse Excel file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsArrayBuffer(file);
  });
};

export const generateCSV = (data: VarBillingRow[]): string => {
  const headers = [
    'External Contract ID', 'Customer Code', 'Product Code', 'Quantity',
    'Price', 'Net', 'Comment', 'Errors'
  ];
  const rows = data.map(row => [
    row.externalContractId,
    row.customerCode,
    row.productCode,
    row.quantity,
    row.price,
    row.net,
    row.comment,
    row.errors || '',
  ]);

  return Papa.unparse([headers, ...rows]);
};

export const generateExcel = (data: VarBillingRow[]): ArrayBuffer => {
  const headers = [
    'External Contract ID', 'Customer Code', 'Product Code', 'Quantity',
    'Price', 'Net', 'Comment', 'Errors'
  ];
  const rows = data.map(row => [
    row.externalContractId,
    row.customerCode,
    row.productCode,
    row.quantity,
    row.price,
    row.net,
    row.comment,
    row.errors || '',
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'VarBilling Results');

  return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
};

export const parseFile = async (file: File, columnMappings?: Map<string, string>): Promise<VarBillingRow[]> => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  if (fileExtension === 'csv') {
    return parseCSV(file, columnMappings);
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    return parseExcel(file, columnMappings);
  } else {
    throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }
};

export const downloadFile = (content: string | ArrayBuffer, filename: string, type: 'csv' | 'excel' = 'csv') => {
  let blob: Blob;

  if (type === 'excel') {
    blob = new Blob([content as ArrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  } else {
    blob = new Blob([content as string], { type: 'text/csv;charset=utf-8;' });
  }

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
